import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { X, AlertCircle, Users, Building2, UsersRound } from 'lucide-react';
import { CommanServices, normalizeIncidentRecipientDirectory } from '@/services/commanServices';
import { Checkbox } from '@/components/ui/checkbox';
import ScenarioRoleField, { RoleGuidanceBanner, blankRole, buildScenarioRoleOverridePatch, canOverrideScenarioRoles, cleanRoles, markScenarioRolesForOverride, rolesValid, rolesSummary } from './ScenarioRoleField';
import ConditionalFollowupTrigger from './ConditionalFollowupTrigger';

import axios from 'axios';
import { ApiEndPoint } from '@/services/apiUttiLities';
import { DutyBadge } from './PersonnelOfferPrompt';
import RecipientPicker from './RecipientPicker';
import { COMPLETE_EVENT } from '@/context/DutyCheckJobContext';

const POLL_DUTY_MS = 15000;

const ManualNotificationModal = ({
  isOpen,
  onClose,
  incident,
  editingItem = null,
  onSubmit,
  isAdmin,
  managedDepartmentIds = [],
  userRoleMap = {},
  conditionalContext = null,
  onAddConditional = null,
  scenarioStaffingDefaults = null,
  onRoleCatalogUpsert = null,
  onlineUserIds = null,
  presenceByUserId = {},
  allowedRecipientUserIds = null,
  allowedRecipientDepartmentIds = null,
  allowedRecipientTeamIds = null,
  allowedRecipientCrewIds = null,
}) => {
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [crewOptions, setCrewOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [targetTab, setTargetTab] = useState('users');
  const [slaMinutes, setSlaMinutes] = useState('');

  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [selectedDepartmentIds, setSelectedDepartmentIds] = useState([]);
  const [selectedCrewIds, setSelectedCrewIds] = useState([]);
  const [roles, setRoles] = useState([blankRole()]);
  const [note, setNote] = useState('');
  const [roleOverrideMeta, setRoleOverrideMeta] = useState({});

  const isEditing = !!editingItem;
  // Requested-stage exercises author injects exactly like the Scenario page (full role-based layout).
  // Role-planning mode: scenario page, exercise at Requested, or a REAL incident
  // reviewing its scenario plan (cards join the plan and fire on activation).
  const isScenario = !!incident?.is_scenario || (!!incident?.is_exercise && String(incident?.status || '').toLowerCase() === 'requested') || (!incident?.is_exercise && incident?.plan_state === 'planned');
  // Real incident reviewing its plan: planning layout (roles optional) but LIVE
  // recipient pickers and a real Send — it's an emergency, nothing is disabled.
  const realPlanReview = !incident?.is_exercise && incident?.plan_state === 'planned';
  // + button on a REAL incident: recipients first, each selection expands for an
  // optional role + SLA; no planning blocks, no conditional follow-ups.
  const realCreate = !editingItem && !incident?.is_exercise && !incident?.is_scenario;
  const planCardEdit = !!editingItem && editingItem?.plan_state === 'planned' && !incident?.is_exercise;
  const scenarioPulledEdit = canOverrideScenarioRoles(incident, editingItem);
  const [pickerSelections, setPickerSelections] = useState([]);
  const [recipientConfig, setRecipientConfig] = useState({});
  const setRecipientField = (key, field, value) =>
    setRecipientConfig((prev) => ({ ...prev, [key]: { ...(prev[key] || {}), [field]: value } }));
  // Roles already on the card being edited (scenarioRoles or roleAssignments).
  const editingCardRoles = useMemo(() => {
    const m = editingItem?.meta || {};
    const fromRoles = cleanRoles(m.scenarioRoles);
    if (fromRoles.length) return fromRoles.map((r) => r.role);
    const ra = m.roleAssignments;
    if (ra && typeof ra === 'object') return Object.keys(ra).filter(Boolean);
    if (m.role) return [String(m.role)];
    return [];
  }, [editingItem]);
  const RecipientTuning = ({ cfgKey }) => {
    const roleVal = recipientConfig[cfgKey]?.role || '';
    return (
    <div className="flex items-center gap-2 pl-9 pr-3 pb-2 bg-indigo-50/40" onClick={(e) => e.stopPropagation()}>
      <div className="flex-1 flex items-center gap-1.5 min-w-0">
        <input
          value={roleVal}
          onChange={(e) => setRecipientField(cfgKey, 'role', e.target.value)}
          placeholder="Role (optional)"
          data-testid={`recipient-role-${cfgKey}`}
          className="flex-1 min-w-0 text-xs border border-gray-300 rounded px-2 py-1 bg-white"
        />
        {roleVal.trim() ? (
          <span
            data-testid={`recipient-role-chip-${cfgKey}`}
            className="shrink-0 inline-flex items-center rounded-full border border-violet-300 bg-violet-50 px-2 py-0.5 text-[10px] font-semibold text-violet-700"
          >
            {roleVal.trim()}
          </span>
        ) : null}
      </div>
      <input
        type="number"
        min="0"
        value={recipientConfig[cfgKey]?.sla || ''}
        onChange={(e) => setRecipientField(cfgKey, 'sla', e.target.value)}
        placeholder="SLA min (optional)"
        data-testid={`recipient-sla-${cfgKey}`}
        className="w-32 text-xs border border-gray-300 rounded px-2 py-1 bg-white"
      />
    </div>
    );
  };
  const isRequested = false;
  // Active-stage exercise: recipients are individual drill participants only (no dept/crew broadcast),
  // and each user is labelled with the role they were assigned during the Requested stage.
  const isActiveExercise = !!incident?.is_exercise && String(incident?.status || '').toLowerCase() === 'active';

  useEffect(() => {
    if (!isOpen) return;
    // Prefill from the card being edited (re-target), else start empty.
    const m = editingItem?.meta || {};
    setRoleOverrideMeta(m);
    const preUsers = Array.isArray(m.recipientIds) && m.recipientIds.length
      ? m.recipientIds.map(String)
      : (m.user_id || m.userId || (editingItem?.actor?.id && editingItem.actor.type === 'user' ? editingItem.actor.id : null))
        ? [String(m.user_id || m.userId || editingItem?.actor?.id)]
        : [];
    const preDepts = (m.department_id || m.departmentId) ? [String(m.department_id || m.departmentId)] : [];
    const preCrews = (m.crewId || m.crew_id) ? [String(m.crewId || m.crew_id)] : [];
    setSelectedUserIds(preUsers);
    setSelectedDepartmentIds(preDepts);
    setSelectedCrewIds(preCrews);
    setSlaMinutes(m.sla?.withinMinutes != null ? String(m.sla.withinMinutes) : '');
    setRoles(Array.isArray(m.scenarioRoles) && m.scenarioRoles.length
      ? markScenarioRolesForOverride(m.scenarioRoles.map((r) => ({
        ...r,
        sla: r.sla == null ? '' : r.sla,
      })), scenarioPulledEdit)
      : [blankRole()]);
    setNote(m.assignmentNote || '');
    // Conditional follow-up: prefill role(s) + note from the source card when creating new.
    if (!editingItem && conditionalContext?.sourceRoles?.length) {
      setRoles(conditionalContext.sourceRoles.map((r) => ({ ...r, sla: r.sla == null ? '' : r.sla })));
      if (conditionalContext.sourceNote) setNote(conditionalContext.sourceNote);
    }
    setUserSearch('');
    setTargetTab(isActiveExercise ? 'users' : (preDepts.length ? 'departments' : preCrews.length ? 'crews' : 'users'));
    setShowConfirmation(false);

    const load = async () => {
      try {
        if (!isAdmin && incident?.id) {
          const scopedResponse = await CommanServices.getIncidentRecipientDirectory(incident.id);
          const scoped = normalizeIncidentRecipientDirectory(scopedResponse);
          setUsers(scoped.users);
          setDepartments(scoped.departments);
          setCrewOptions(scoped.crews.filter((crew) => crew?.is_active !== false));
          setDutyMap(scoped.duty);
          return;
        }
        const [uRes, dRes, tRes] = await Promise.all([
          CommanServices.getAppUsers(),
          CommanServices.departments(),
          isAdmin ? CommanServices.getTeamsData() : Promise.resolve(null),
        ]);

        const userList = Array.isArray(uRes?.data) ? uRes.data : [];
        setUsers(userList);

        const deptList = Array.isArray(dRes?.data) ? dRes.data : [];
        setDepartments(deptList);

        if (isAdmin && tRes && Array.isArray(tRes.data)) {
          const crews = [];
          tRes.data.forEach((team) => {
            (team.crews || []).forEach((c) => {
              if (c.id && c.is_active !== false) {
                crews.push({
                  id: c.id,
                  crew_name: c.crew_name || 'Crew',
                  team_name: team.team_name || 'Team',
                });
              }
            });
          });
          setCrewOptions(crews);
        } else {
          setCrewOptions([]);
        }
      } catch (e) {
        console.error('Error loading manual notification data:', e);
      }
    };
    load();
  }, [isOpen, isAdmin, editingItem, incident?.id]);

  const visibleUsers = useMemo(() => {
    if (isAdmin) return users;
    if (Array.isArray(allowedRecipientUserIds)) {
      const allowed = new Set(allowedRecipientUserIds.map(String));
      return users.filter((user) => allowed.has(String(user.id)));
    }
    if (!managedDepartmentIds?.length) return [];
    return users.filter((u) => {
      const dus = u.departments_units || [];
      const du = u.department_unit ? [u.department_unit] : [];
      const all = [...dus, ...du].filter(Boolean);
      return all.some((x) => managedDepartmentIds.includes(x));
    });
  }, [users, isAdmin, managedDepartmentIds, allowedRecipientUserIds]);

  const visibleDepartments = useMemo(() => {
    if (isAdmin) return departments;
    if (Array.isArray(allowedRecipientDepartmentIds)) {
      const allowed = new Set(allowedRecipientDepartmentIds.map(String));
      return departments.filter((department) => (
        allowed.has(String(department.id || ''))
        || allowed.has(String(department.department_name || ''))
      ));
    }
    return departments.filter((d) => managedDepartmentIds.includes(d.id));
  }, [departments, isAdmin, managedDepartmentIds, allowedRecipientDepartmentIds]);

  const [dutyMap, setDutyMap] = useState({});
  const fetchDutyMap = useCallback(async () => {
    try {
      if (!isAdmin && incident?.id) {
        const scopedResponse = await CommanServices.getIncidentRecipientDirectory(incident.id);
        setDutyMap(normalizeIncidentRecipientDirectory(scopedResponse).duty);
        return;
      }
      const r = await axios.get(`${ApiEndPoint}/api/duty-status`, { headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } });
      setDutyMap(r.data?.data?.users || {});
    } catch {
      setDutyMap({});
    }
  }, [incident?.id, isAdmin]);

  useEffect(() => {
    if (!isOpen) return;
    fetchDutyMap();
  }, [isOpen, fetchDutyMap]);

  useEffect(() => {
    if (!isOpen) return;
    const refresh = () => { fetchDutyMap(); };
    window.addEventListener(COMPLETE_EVENT, refresh);
    const interval = setInterval(refresh, POLL_DUTY_MS);
    return () => {
      window.removeEventListener(COMPLETE_EVENT, refresh);
      clearInterval(interval);
    };
  }, [isOpen, fetchDutyMap]);

  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    const rank = (id) => (dutyMap[String(id)]?.status === 'in_duty' ? 0 : dutyMap[String(id)]?.status === 'not_in_duty' ? 2 : 1);
    const base = !q ? visibleUsers : visibleUsers.filter(
      (u) =>
        (u.name || '').toLowerCase().includes(q) ||
        (u.email || '').toLowerCase().includes(q) ||
        (u.position || '').toLowerCase().includes(q)
    );
    return [...base].sort((a, b) => rank(a.id) - rank(b.id));
  }, [visibleUsers, userSearch, dutyMap]);

  const toggleUser = (id) => {
    setSelectedUserIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleDepartment = (id) => {
    setSelectedDepartmentIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleCrew = (id) => {
    setSelectedCrewIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const estimatedTotal =
    selectedUserIds.length + selectedDepartmentIds.length + selectedCrewIds.length;
  const notificationRecipientCount = pickerSelections.length;

  const hasOffDutySelected = useMemo(() => {
    return selectedUserIds.some((id) => {
      const u = users.find((x) => x.id === id);
      return u && u.enable_duty_check === false;
    });
  }, [selectedUserIds, users]);

  const sendNotification = async (force = false) => {
    if (isScenario && (planCardEdit || !realPlanReview)) {
      if (!rolesValid(roles, incident?.is_scenario ? scenarioStaffingDefaults : null)) {
        alert('Add at least one role.');
        return;
      }
      setLoading(true);
      try {
        const overridePatch = scenarioPulledEdit
          ? buildScenarioRoleOverridePatch(roleOverrideMeta, roles)
          : null;
        if (overridePatch?.error) {
          alert(overridePatch.error);
          setLoading(false);
          return;
        }
        const clean = overridePatch?.scenarioRoles || cleanRoles(roles);
        const meta = {
          manuallyCreated: true,
          addedDuringActive: isActiveExercise,
          scenarioRoles: clean,
          ...(overridePatch ? {
            roleAssignments: overridePatch.roleAssignments,
            history: overridePatch.history,
            roleOverrideHistory: overridePatch.roleOverrideHistory,
          } : {}),
          assignmentNote: note || '',
          sla: slaMinutes !== '' && slaMinutes != null && !(planCardEdit && Number(slaMinutes) <= 0) ? { withinMinutes: Number(slaMinutes) } : null,
          ...(conditionalContext?.conditional ? { conditional: conditionalContext.conditional } : {}),
        };
        const payload = {
          incident_id: incident.id,
          group: 'notification',
          timestamp: new Date().toISOString(),
          actor: { id: 'role', name: rolesSummary(clean), type: 'role' },
          title: 'Role-based notification',
          details: rolesSummary(clean),
          chips: [],
          attachments: [],
          meta,
        };
        if (isEditing && editingItem?.id) {
          await CommanServices.updatePutIncidentTimeline(incident.id, editingItem.id, {
            ...editingItem,
            ...payload,
            meta: { ...(editingItem.meta || {}), ...meta },
          });
        } else {
          await CommanServices.addincidentTimeline(incident.id, payload);
        }
        await onSubmit();
        handleClose();
      } catch (error) {
        console.error('Scenario role notification error:', error);
        alert(error?.message || 'Failed to save roles');
      } finally {
        setLoading(false);
      }
      return;
    }

    // Unified recipient list: RecipientPicker selections (realCreate) or the
    // legacy tab states — each entry carries its optional role + SLA.
    const effUsers = realCreate
      ? pickerSelections.filter((x) => x.type === 'user').map((x) => ({ id: x.id, role: x.role, sla: x.sla }))
      : selectedUserIds.map((id) => ({ id, role: (recipientConfig[`u:${id}`] || {}).role, sla: (recipientConfig[`u:${id}`] || {}).sla }));
    const effDepts = realCreate
      ? pickerSelections.filter((x) => x.type === 'department').map((x) => ({ id: x.id, role: x.role, sla: x.sla }))
      : selectedDepartmentIds.map((id) => ({ id, role: (recipientConfig[`d:${id}`] || {}).role, sla: (recipientConfig[`d:${id}`] || {}).sla }));
    const effCrews = realCreate
      ? pickerSelections.filter((x) => x.type === 'crew').map((x) => ({ id: x.id, role: x.role, sla: x.sla }))
      : selectedCrewIds.map((id) => ({ id, role: (recipientConfig[`c:${id}`] || {}).role, sla: (recipientConfig[`c:${id}`] || {}).sla }));

    if (effUsers.length === 0 && effDepts.length === 0 && effCrews.length === 0) {
      alert('Choose at least one recipient: users, department(s), or crew(s).');
      return;
    }

    if (!isAdmin && !realCreate && effCrews.length > 0) {
      alert('Only administrators can broadcast to crews.');
      return;
    }

    if (hasOffDutySelected && !force) {
      setShowConfirmation(true);
      return;
    }

    setLoading(true);
    try {
      const handleResponse = async (raw) => {
        if (raw && raw.status === false) {
          const m =
            typeof raw.message === 'string'
              ? raw.message
              : JSON.stringify(raw.message || '');
          if (
            !force &&
            (m.toLowerCase().includes('off duty') || m.toLowerCase().includes('force'))
          ) {
            if (window.confirm('Some recipients are off duty. Send anyway?')) {
              setLoading(false);
              return sendNotification(true);
            }
            return false;
          }
          alert(m || 'Failed to send notification');
          return false;
        }
        const inner = raw?.data != null && typeof raw.data === 'object' ? raw.data : raw;
        if (inner?.success === false) {
          alert(inner.message || 'Failed to send notification');
          return false;
        }
        return true;
      };

      const slaPayload = slaMinutes !== '' && slaMinutes != null ? { sla_minutes: Number(slaMinutes) } : {};

      for (const eu of effUsers) {
        const uid = eu.id;
        const _u = users.find((x) => x.id === uid);
        const _cfg = eu;
        const _role = (_cfg.role || '').trim() || userRoleMap[String(uid)] || (_u && userRoleMap[String(_u.name)]) || null;
        const _raPayload = _role ? { roleAssignments: { [_role]: [{ id: uid, name: _u?.name || uid }] } } : {};
        const _sla = _cfg.sla !== '' && _cfg.sla != null && Number(_cfg.sla) > 0 ? { sla_minutes: Number(_cfg.sla) } : slaPayload;
        const raw = await CommanServices.notificationMaunal(incident.id, {
          user_ids: [uid],
          department_ids: [],
          crew_ids: [],
          force,
          ..._sla,
          ..._raPayload,
        });
        if (!(await handleResponse(raw))) return;
      }

      for (const ed of effDepts) {
        const did = ed.id;
        const _cfg = ed;
        const _sla = _cfg.sla !== '' && _cfg.sla != null && Number(_cfg.sla) > 0 ? { sla_minutes: Number(_cfg.sla) } : slaPayload;
        const raw = await CommanServices.notificationMaunal(incident.id, {
          user_ids: [],
          department_ids: [did],
          crew_ids: [],
          force,
          ..._sla,
          ...((_cfg.role || '').trim() ? { role_label: _cfg.role.trim() } : {}),
        });
        if (!(await handleResponse(raw))) return;
      }

      if (isAdmin || realCreate) {
        for (const ec of effCrews) {
          const cid = ec.id;
          const _cfg = ec;
          const _sla = _cfg.sla !== '' && _cfg.sla != null && Number(_cfg.sla) > 0 ? { sla_minutes: Number(_cfg.sla) } : slaPayload;
          const raw = await CommanServices.notificationMaunal(incident.id, {
            user_ids: [],
            department_ids: [],
            crew_ids: [cid],
            force,
            ..._sla,
            ...((_cfg.role || '').trim() ? { role_label: _cfg.role.trim() } : {}),
          });
          if (!(await handleResponse(raw))) return;
        }
      }

      // Re-target: when editing an existing notification card, remove the old one
      // so the new selection replaces it.
      if (isEditing && editingItem?.id) {
        try { await CommanServices.deleteTimelineItem(incident.id, editingItem.id); } catch (e) { /* noop */ }
      }

      await onSubmit();
      handleClose();
    } catch (error) {
      console.error('Manual notification error:', error);
      alert(error?.message || 'Request failed');
    } finally {
      setLoading(false);
    }
  };

  const handleForceConfirm = () => {
    setShowConfirmation(false);
    sendNotification(true);
  };

  const handleClose = () => {
    setSelectedUserIds([]);
    setSelectedDepartmentIds([]);
    setSelectedCrewIds([]);
    setShowConfirmation(false);
    setUserSearch('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className={`bg-white rounded-lg shadow-xl w-full max-h-[90vh] flex flex-col my-4 ${realCreate ? 'max-w-2xl' : 'max-w-lg'}`}>
        <div className="flex items-center justify-between p-4 border-b shrink-0">
          <h3 className="text-lg font-semibold text-gray-900">{isEditing ? 'Edit notification recipients' : 'Send manual notification'}</h3>
          <button type="button" aria-label="Close manual notification" onClick={handleClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        {showConfirmation && (
          <div className="absolute inset-0 bg-black bg-opacity-40 rounded-lg flex items-center justify-center p-4 z-10">
            <div className="bg-white rounded-lg p-5 shadow-lg w-full max-w-sm">
              <div className="text-center">
                <AlertCircle className="h-10 w-10 text-orange-500 mx-auto mb-3" />
                <h4 className="text-base font-semibold text-gray-900 mb-2">Off-duty recipients</h4>
                <p className="text-sm text-gray-600 mb-4">
                  At least one selected user is off duty. Send a force notification to everyone in this
                  broadcast?
                </p>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setShowConfirmation(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleForceConfirm}
                    className="flex-1 px-4 py-2 bg-orange-600 rounded text-sm font-medium text-white hover:bg-orange-700 disabled:opacity-50"
                    disabled={loading}
                  >
                    {loading ? 'Sending…' : 'Confirm'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="p-4 space-y-3 overflow-y-auto flex-1">
          {!incident?.is_scenario && !realCreate && (
          <p className="text-xs text-gray-600">
            {isActiveExercise
              ? 'Notify specific drill participants. Each user keeps the role they were assigned during planning.'
              : (isAdmin
                ? 'Admins can notify specific users, whole departments, or crews in one action.'
                : 'You can notify users or whole departments within the department(s) you lead.')}
          </p>
          )}

          {isScenario && !realCreate && (
            <ScenarioRoleField roles={roles} setRoles={setRoles} note={note} setNote={setNote} slaValue={slaMinutes} onSlaChange={setSlaMinutes} slaLabel="SLA — Acknowledgment (minutes)" hideIntro={planCardEdit} slaZeroLabel={planCardEdit ? '(0 or empty = no SLA)' : '(0 = immediate)'} roleCatalog={incident?.is_scenario ? scenarioStaffingDefaults : null} onRoleCatalogUpsert={onRoleCatalogUpsert} lockScenarioDefaultRoles={scenarioPulledEdit} roleAssignments={roleOverrideMeta?.roleAssignments || {}} />
          )}
          {isScenario && !realCreate && (
            <ConditionalFollowupTrigger editingItem={editingItem} onAddConditional={onAddConditional} />
          )}
          {(isRequested || (!isScenario && editingCardRoles.length > 0)) && (
            <RoleGuidanceBanner
              roles={editingItem?.meta?.scenarioRoles?.length
                ? editingItem.meta.scenarioRoles
                : editingCardRoles.map((r) => ({ role: r, count: 1 }))}
              note={editingItem?.meta?.assignmentNote}
            />
          )}
          {!isScenario && editingCardRoles.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5" data-testid="manual-notif-assigned-roles">
              <span className="text-[10px] font-bold uppercase tracking-wider text-violet-600">Assigned role(s):</span>
              {editingCardRoles.map((r) => (
                <span key={r} className="inline-flex items-center rounded-full border border-violet-300 bg-violet-50 px-2 py-0.5 text-[11px] font-semibold text-violet-700">
                  {r}
                </span>
              ))}
            </div>
          )}
          {realCreate && (
            <div>
              <RecipientPicker
                selections={pickerSelections}
                onChange={setPickerSelections}
                testIdPrefix="notif"
                incidentId={incident?.id}
                onlineUserIds={onlineUserIds}
                presenceByUserId={presenceByUserId}
                variant="notification"
                allowedUserIds={allowedRecipientUserIds}
                allowedDepartmentIds={allowedRecipientDepartmentIds}
                allowedTeamIds={allowedRecipientTeamIds}
                allowedCrewIds={allowedRecipientCrewIds}
              />
            </div>
          )}
          {!incident?.is_scenario && (
          <div className={isScenario && !realPlanReview ? 'space-y-3 opacity-50 pointer-events-none select-none' : 'space-y-3'} style={{ display: (planCardEdit || realCreate) ? 'none' : undefined }}>
          {isScenario && !realPlanReview && (
            <p className="text-[11px] text-violet-600 font-medium">Recipient pickers are disabled while planning a scenario.</p>
          )}
          <div className="flex rounded-lg border border-gray-200 p-0.5 bg-gray-50 text-xs" style={{ display: isActiveExercise ? 'none' : 'flex' }}>
            <button
              type="button"
              className={`flex-1 flex items-center justify-center gap-1 py-2 rounded-md ${
                targetTab === 'users' ? 'bg-white shadow text-gray-900' : 'text-gray-600'
              }`}
              onClick={() => setTargetTab('users')}
            >
              <Users className="h-3.5 w-3.5" />
              Users
            </button>
            {!isActiveExercise && (
            <button
              type="button"
              className={`flex-1 flex items-center justify-center gap-1 py-2 rounded-md ${
                targetTab === 'departments' ? 'bg-white shadow text-gray-900' : 'text-gray-600'
              }`}
              onClick={() => setTargetTab('departments')}
            >
              <Building2 className="h-3.5 w-3.5" />
              Departments
            </button>
            )}
            {isAdmin && !isActiveExercise && (
              <button
                type="button"
                className={`flex-1 flex items-center justify-center gap-1 py-2 rounded-md ${
                  targetTab === 'crews' ? 'bg-white shadow text-gray-900' : 'text-gray-600'
                }`}
                onClick={() => setTargetTab('crews')}
              >
                <UsersRound className="h-3.5 w-3.5" />
                Crews
              </button>
            )}
          </div>

          {targetTab === 'users' && (
            <div className="space-y-2">
              <input
                type="search"
                placeholder="Search users…"
                value={userSearch}
                onChange={(e) => setUserSearch(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              />
              <div className="border border-gray-200 rounded-md max-h-48 overflow-y-auto divide-y">
                {filteredUsers.length === 0 ? (
                  <p className="p-3 text-xs text-gray-500">No users match your access.</p>
                ) : (
                  filteredUsers.map((u) => (
                    <React.Fragment key={u.id}>
                    <label
                      className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm"
                    >
                      <Checkbox
                        checked={selectedUserIds.includes(u.id)}
                        onCheckedChange={() => toggleUser(u.id)}
                      />
                      <span className="flex-1">
                        {u.name}
                        {(() => {
                          if (realCreate) return null;
                          const role = userRoleMap[String(u.id)] || userRoleMap[String(u.name)] || (isActiveExercise ? null : u.position);
                          if (!role) return null;
                          return (
                            <span className={isActiveExercise ? 'text-emerald-700 text-xs font-medium' : 'text-gray-500 text-xs'}> — {role}</span>
                          );
                        })()}
                      </span>
                      {onlineUserIds?.has?.(String(u.id)) && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700" title="Connected to the mobile app now">
                          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Online now
                        </span>
                      )}
                      <DutyBadge status={dutyMap[String(u.id)]?.status} />
                      {u.enable_duty_check === false && (
                        <span className="text-[10px] uppercase text-red-600">Off duty</span>
                      )}
                    </label>
                    {realCreate && selectedUserIds.includes(u.id) && <RecipientTuning cfgKey={`u:${u.id}`} />}
                    </React.Fragment>
                  ))
                )}
              </div>
            </div>
          )}

          {targetTab === 'departments' && (
            <div className="border border-gray-200 rounded-md max-h-56 overflow-y-auto divide-y">
              {visibleDepartments.length === 0 ? (
                <p className="p-3 text-xs text-gray-500">No departments available.</p>
              ) : (
                visibleDepartments.map((d) => (
                  <React.Fragment key={d.id}>
                  <label
                    className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm"
                  >
                    <Checkbox
                      checked={selectedDepartmentIds.includes(d.id)}
                      onCheckedChange={() => toggleDepartment(d.id)}
                    />
                    <span>{d.department_name || d.name || d.id}</span>
                  </label>
                  {realCreate && selectedDepartmentIds.includes(d.id) && <RecipientTuning cfgKey={`d:${d.id}`} />}
                  </React.Fragment>
                ))
              )}
            </div>
          )}

          {targetTab === 'crews' && isAdmin && (
            <div className="border border-gray-200 rounded-md max-h-56 overflow-y-auto divide-y">
              {crewOptions.length === 0 ? (
                <p className="p-3 text-xs text-gray-500">No crews found.</p>
              ) : (
                crewOptions.map((c) => (
                  <React.Fragment key={c.id}>
                  <label
                    className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer text-sm"
                  >
                    <Checkbox
                      checked={selectedCrewIds.includes(c.id)}
                      onCheckedChange={() => toggleCrew(c.id)}
                    />
                    <span>
                      {c.crew_name}
                      <span className="text-gray-500 text-xs"> — {c.team_name}</span>
                    </span>
                  </label>
                  {realCreate && selectedCrewIds.includes(c.id) && <RecipientTuning cfgKey={`c:${c.id}`} />}
                  </React.Fragment>
                ))
              )}
            </div>
          )}

          {estimatedTotal > 0 && (
            <p className="text-xs text-gray-700">
              Selection summary: {selectedUserIds.length} user(s), {selectedDepartmentIds.length}{' '}
              department(s), {selectedCrewIds.length} crew(s). Overlaps are deduplicated on the server.
            </p>
          )}

          </div>
          )}

          {!isScenario && !realCreate && (
          <div className="pt-1">
            <label className="text-xs font-semibold text-gray-700">SLA — Acknowledgment (minutes)</label>
            <input
              type="number"
              min="0"
              value={slaMinutes}
              onChange={(e) => setSlaMinutes(e.target.value)}
              placeholder="e.g. 5"
              data-testid="manual-notification-sla"
              className="mt-1 w-full text-sm border border-gray-300 rounded-md px-2.5 py-2 text-gray-900 bg-white"
            />
            <p className="text-[11px] text-gray-500 mt-1">Expected time for recipients to acknowledge once notified.</p>
          </div>
          )}
        </div>

        <div className={`flex items-center gap-2 p-4 border-t shrink-0 ${realCreate ? 'justify-between' : 'justify-end'}`}>
          {realCreate && (
            <p className="text-xs font-medium text-slate-600" data-testid="manual-notification-recipient-count">
              {notificationRecipientCount} recipient{notificationRecipientCount === 1 ? '' : 's'} will be notified
            </p>
          )}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => sendNotification(false)}
              disabled={loading || ((isScenario && (planCardEdit || !realPlanReview)) ? !rolesValid(roles, incident?.is_scenario ? scenarioStaffingDefaults : null) : (realCreate ? pickerSelections.length === 0 : estimatedTotal === 0))}
              className={`px-4 py-2 rounded-md text-sm font-medium text-white disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-500 ${realCreate ? 'bg-violet-600 hover:bg-violet-700' : 'bg-blue-600 hover:bg-blue-700'}`}
            >
              {loading
                ? (realCreate ? 'Sending…' : 'Saving…')
                : ((isScenario && (planCardEdit || !realPlanReview))
                  ? 'Save roles'
                  : (isEditing ? 'Save changes' : (realCreate ? `Send (${notificationRecipientCount})` : 'Send')))}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManualNotificationModal;
